import React, { useState } from 'react';

/*
 * A small, safe Markdown renderer for RiskLo AI answers: paragraphs, headings,
 * lists, tables, fenced code (with Copy), `code`, **bold**, *italic*, links to
 * RiskLo pages ([Label](page:key)), and https links to a short list of vetted
 * outside sites (prop firms' own pages, major news). Everything is React text,
 * never HTML, so nothing in an answer can inject markup or scripts.
 */

/** Pages an answer may link to (mirrors PAGES in server/services/aiAssistant/prompt.js). */
export const ASK_PAGES = {
  dashboard: 'Overview',
  'client-reporting': 'My Portfolio',
  results: 'Results',
  'portfolio-simulator': 'Portfolio Simulator',
  'blowout-risk': 'Blowout Risk',
  'rule-risk': '% Rule Risk',
  'hedging-risk': 'Hedging Risk',
  'prop-firm-planner': 'Prop Firm Planner',
  'trade-watcher': 'Trade Watcher',
  'position-sizer': 'Position Sizer',
  'concentration-risk': 'Concentration Risk',
  'drawdown-risk': 'Drawdown & VaR',
  'stop-planner': 'Stop Planner',
  rebalance: 'Rebalancing',
  'how-we-calculate': 'How We Calculate',
  faq: 'FAQ',
  'consistency-rules': 'Consistency Rules',
  tutorials: 'Tutorials',
  'how-to-export-csv': 'How to Export CSV',
  account: 'Account',
  security: 'Security & Data',
  'privacy-policy': 'Privacy Policy',
  'terms-and-conditions': 'Terms',
  pricing: 'Pricing',
  contact: 'Contact',
  about: 'About',
  updates: 'Update Log',
  'risk-tools': 'Risk Tools',
  'my-results': 'My Results',
  help: 'Help',
  features: 'Features',
  companies: 'For Companies',
  'brand-strategies': 'Brand Strategies (Brand Admins)',
  'brand-settings': 'Brand Settings (Brand Admins)',
};

/** Outside sites an answer may link to: prop firms' own pages and the news sources deep answers search. */
export const LINK_DOMAINS = [
  'apextraderfunding.com', 'topstep.com', 'bulenox.com', 'takeprofittrader.com', 'myfundedfutures.com',
  'reuters.com', 'apnews.com', 'bloomberg.com', 'cnbc.com', 'wsj.com', 'ft.com', 'marketwatch.com', 'barrons.com',
  'finance.yahoo.com', 'nasdaq.com', 'cmegroup.com', 'federalreserve.gov', 'bls.gov', 'bea.gov', 'treasury.gov',
  'sec.gov', 'finra.org', 'investopedia.com', 'coindesk.com', 'theblock.co', 'spglobal.com', 'morningstar.com',
  'stlouisfed.org', 'nyse.com', 'cboe.com', 'census.gov',
  'tradeify.co', 'lucidtrading.com', 'alpha-futures.com', 'fundednextfutures.com', 'fundednext.com', 'elitetraderfunding.com',
  'earn2trade.com', 'toponefutures.com', 'tradeday.com', 'fundingticks.com', 'ftmo.com',
];

export function safeOutsideUrl(href) {
  try {
    const u = new URL(href);
    if (u.protocol !== 'https:') return null;
    const host = u.hostname.toLowerCase();
    return LINK_DOMAINS.some((d) => host === d || host.endsWith(`.${d}`)) ? u.toString() : null;
  } catch (_e) {
    return null;
  }
}

const INLINE = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\)|\*[^*\s][^*]*\*)/g;

function renderInline(text, onOpenPage, keyBase) {
  const out = [];
  let last = 0;
  let i = 0;
  String(text).replace(INLINE, (m, _g, offset) => {
    if (offset > last) out.push(text.slice(last, offset));
    const k = `${keyBase}-${i += 1}`;
    if (m.startsWith('**')) out.push(<strong key={k}>{m.slice(2, -2)}</strong>);
    else if (m.startsWith('`')) out.push(<code key={k}>{m.slice(1, -1)}</code>);
    else if (m.startsWith('[')) {
      const [, label, href] = m.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/) || [];
      const page = href && href.startsWith('page:') ? href.slice(5) : null;
      const outside = !page && href ? safeOutsideUrl(href) : null;
      const symbolPage = page && /^symbol\/[A-Za-z0-9.^=-]{1,15}$/.test(page);
      if (page && (ASK_PAGES[page] || symbolPage) && onOpenPage) {
        out.push(<button key={k} type="button" className="ask-link" onClick={() => onOpenPage(page)}>{label}</button>);
      } else if (outside) {
        out.push(<a key={k} className="ask-link" href={outside} target="_blank" rel="noopener noreferrer nofollow">{label} ↗</a>);
      } else {
        out.push(label || m); // Links anywhere else show as plain text.
      }
    } else out.push(<em key={k}>{m.slice(1, -1)}</em>);
    last = offset + m.length;
    return m;
  });
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function CodeBlock({ code, lang }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(code).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {});
  };
  return (
    <div className="ask-code">
      <div className="ask-code-head"><span>{lang || 'text'}</span><button type="button" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button></div>
      <pre><code>{code}</code></pre>
    </div>
  );
}

const isTableRow = (l) => /^\s*\|.*\|\s*$/.test(l);
const isDivider = (l) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(l);
const cells = (l) => l.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());

/** Blocks from Markdown text. */
export function parseBlocks(text) {
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const fence = line.match(/^\s*```(\w*)\s*$/);
    if (fence) {
      const body = [];
      i += 1;
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i])) { body.push(lines[i]); i += 1; }
      blocks.push({ type: 'code', lang: fence[1], code: body.join('\n') });
      i += 1;
    } else if (!line.trim()) {
      i += 1;
    } else if (/^#{1,4}\s/.test(line)) {
      blocks.push({ type: 'heading', text: line.replace(/^#{1,4}\s+/, '') });
      i += 1;
    } else if (isTableRow(line) && i + 1 < lines.length && isDivider(lines[i + 1])) {
      const head = cells(line);
      const rows = [];
      i += 2;
      while (i < lines.length && isTableRow(lines[i])) { rows.push(cells(lines[i])); i += 1; }
      blocks.push({ type: 'table', head, rows });
    } else if (/^\s*([-*•]|\d+[.)])\s+/.test(line)) {
      const ordered = /^\s*\d+[.)]/.test(line);
      const items = [];
      while (i < lines.length && /^\s*([-*•]|\d+[.)])\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*([-*•]|\d+[.)])\s+/, ''));
        i += 1;
      }
      blocks.push({ type: 'list', ordered, items });
    } else {
      const para = [];
      while (i < lines.length && lines[i].trim() && !/^\s*```/.test(lines[i]) && !/^#{1,4}\s/.test(lines[i])
        && !/^\s*([-*•]|\d+[.)])\s+/.test(lines[i]) && !(isTableRow(lines[i]) && isDivider(lines[i + 1] || ''))) {
        para.push(lines[i]);
        i += 1;
      }
      blocks.push({ type: 'p', text: para.join('\n') });
    }
  }
  return blocks;
}

export default function Answer({ text, onOpenPage }) {
  return (
    <div className="ask-answer">
      {parseBlocks(text).map((b, n) => {
        const k = `b${n}`;
        if (b.type === 'code') return <CodeBlock key={k} code={b.code} lang={b.lang} />;
        if (b.type === 'heading') return <h4 key={k}>{renderInline(b.text, onOpenPage, k)}</h4>;
        if (b.type === 'list') {
          const Tag = b.ordered ? 'ol' : 'ul';
          return <Tag key={k}>{b.items.map((it, j) => <li key={`${k}-${j}`}>{renderInline(it, onOpenPage, `${k}-${j}`)}</li>)}</Tag>;
        }
        if (b.type === 'table') {
          return (
            <div key={k} className="ask-table-wrap">
              <table>
                <thead><tr>{b.head.map((h, j) => <th key={`${k}-h${j}`}>{renderInline(h, onOpenPage, `${k}-h${j}`)}</th>)}</tr></thead>
                <tbody>{b.rows.map((r, j) => <tr key={`${k}-r${j}`}>{r.map((c, x) => <td key={`${k}-${j}-${x}`}>{renderInline(c, onOpenPage, `${k}-${j}-${x}`)}</td>)}</tr>)}</tbody>
              </table>
            </div>
          );
        }
        return <p key={k}>{renderInline(b.text, onOpenPage, k)}</p>;
      })}
    </div>
  );
}
